import { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ANIMATIONS_ENABLED } from '../../config/animations';
import { DESKTOP_PX, desktopPx } from './desktopFallbackScale';
import { QR_RIM_SWEEP_OVERSIZE, QR_RIM_SWEEP_SECONDS, buildSweepingRimRamp } from './qrRimSweep';

interface SweepingQrRimProps {
  /** The ramp's leading colour, out of the top-left at rest. */
  startColor: string;
  /** The ramp's trailing colour, into the bottom-right at rest. */
  endColor: string;
  /** The scan card. Painted over the turning layer, which only the rim's edge ever shows. */
  children: ReactNode;
}

/**
 * The gradient frame around the scan card, with its ramp turning slowly around it.
 *
 * The rim is a padded box carrying the gradient with the card laid over the middle of it, so
 * only a few pixels of the gradient are ever visible — the ring around the card. Turning that
 * ramp is a compositor transform rather than an animated gradient; see qrRimSweep.ts for why,
 * and for the sizing that follows from it.
 *
 * The rim keeps its own structure in here rather than in the screen that uses it because
 * three things have to agree for it to work at all — the layer is oversized, the box clips it,
 * and the card has to be positioned to stack above it. Any one of them undone elsewhere
 * either exposes the turning layer's corners or hides the card behind it.
 */
export const SweepingQrRim = ({ startColor, endColor, children }: SweepingQrRimProps) => {
  const prefersReducedMotion = useReducedMotion();
  const isTurning = ANIMATIONS_ENABLED && !prefersReducedMotion;

  return (
    // Clipped, which is what keeps the oversized layer inside the rounded rim.
    <div
      className='relative overflow-hidden'
      style={{
        padding: desktopPx(DESKTOP_PX.qrRimWidth),
        borderRadius: desktopPx(DESKTOP_PX.qrRimRadius),
      }}
    >
      <motion.div
        aria-hidden
        className='absolute left-1/2 top-1/2'
        style={{
          // Grown past the rim and re-centred on it, so it turns about the rim's centre
          // rather than sliding around it. Percentages on an absolutely positioned element
          // resolve against the rim's padding box, which is the ring itself.
          width: `${QR_RIM_SWEEP_OVERSIZE * 100}%`,
          aspectRatio: '1',
          background: buildSweepingRimRamp(startColor, endColor),
          x: '-50%',
          y: '-50%',
        }}
        // Linear, and a whole turn: any easing would visibly slow the sweep once per lap, and
        // 360° lands exactly where it started so the loop has no seam. At rest — reduced
        // motion, or animations off — it stays at 0°, which is the still gradient the canvas
        // specifies, so nothing is lost by not running.
        animate={isTurning ? { rotate: 360 } : undefined}
        transition={{ duration: QR_RIM_SWEEP_SECONDS, ease: 'linear', repeat: Infinity }}
      />

      {/* Positioned so it paints over the turning layer: an absolutely positioned sibling
        outranks static content in paint order, so without this the card would be behind it. */}
      <div className='relative'>{children}</div>
    </div>
  );
};
